import * as Sentry from '@sentry/browser';

import type { SentryClientInstance, SentryOptions } from '../declarations';
import { getDeployEnv } from './deployEnv';
import { buildDefaultSentryOpts } from './sentry';

type ProcessEnv = Record<string, string | undefined>;

declare const process: { env: ProcessEnv };

type ImportEnv = {
  readonly ODFE_GIT_SHA?: string;
  readonly REACT_APP_GIT_SHA?: string;
};

export const defaultInitSentry = (sentryOptions: SentryOptions): SentryClientInstance => {
  Sentry.init(sentryOptions as Sentry.BrowserOptions);
  return Sentry.getCurrentHub() as unknown as SentryClientInstance;
};

const getGitSha = (): string => {
  let importSha: string | undefined;
  try {
    const importEnv = (import.meta as { env?: ImportEnv }).env;
    importSha = importEnv?.ODFE_GIT_SHA ?? importEnv?.REACT_APP_GIT_SHA;
  } catch {
    // import.meta not available in this runtime
  }

  let odfeProcessSha: string | undefined;
  try {
    odfeProcessSha = process.env.ODFE_GIT_SHA;
  } catch {
    // process is not available or this expression was not replaced by the bundler
  }

  let reactAppProcessSha: string | undefined;
  try {
    reactAppProcessSha = process.env.REACT_APP_GIT_SHA;
  } catch {
    // process is not available or this expression was not replaced by the bundler
  }

  return importSha ?? odfeProcessSha ?? reactAppProcessSha ?? 'unknown';
};

/**
 * TODO:
 * This is kept for backwards compatibility. We should let observability lib handle Sentry initialization,
 * which might require us to build a version of observability for non-consumer apps.
 */
export const loadSentry = (url: string, sentryOptions: Sentry.BrowserOptions = {}) => {
  const env = getDeployEnv();
  defaultInitSentry({
    ...buildDefaultSentryOpts({ appName: '', env, version: getGitSha() }),
    ...sentryOptions,
    dsn: url,
  } as SentryOptions);
};

// Export Sentry directly here so that existing behavior is maintained
// eventually we want to use the "globalObservability.getSentryClient()" proxy
// instance here
export { Sentry };
